import React, { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../lib/api";
import { reportError } from "../../lib/errorReporter";
import { apiErrorMessage, apiFieldErrors } from "../../lib/companyValidation";
import ConfirmDialog from "../ConfirmDialog";

/**
 * Company logo: preview, upload / replace, remove.
 *
 * Duplicate-submit protection is synchronous (a ref), because React state lags
 * one render behind and cannot stop two clicks in the same event-loop tick.
 * Removal always goes through the Phase 2C.2 ConfirmDialog, never a
 * browser-native confirmation dialog.
 */
export const CompanyLogoCard = ({ settings, canEdit, onChanged }) => {
  const { t } = useTranslation();
  const fileRef = useRef(null);
  const uploading = useRef(false);
  const removing = useRef(false);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleFile = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      event.target.value = "";
      if (!file || uploading.current) return;
      uploading.current = true;
      setBusy("upload");
      setError("");
      setNotice("");
      try {
        const body = new FormData();
        body.append("file", file);
        const r = await api.post("/organization/logo", body, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        onChanged(r.data);
        setNotice(t("company.logo_uploaded"));
      } catch (err) {
        reportError("company.logoUpload", err);
        const fields = apiFieldErrors(err);
        setError(fields?.logo || apiErrorMessage(err, t("common.error_generic")));
      } finally {
        uploading.current = false;
        setBusy("");
      }
    },
    [onChanged, t],
  );

  const remove = useCallback(async () => {
    if (removing.current) return;
    removing.current = true;
    setBusy("remove");
    setError("");
    setNotice("");
    try {
      const r = await api.delete("/organization/logo");
      onChanged(r.data);
      setConfirmOpen(false);
      setNotice(t("company.logo_removed"));
    } catch (err) {
      reportError("company.logoRemove", err);
      setError(apiErrorMessage(err, t("common.error_generic")));
    } finally {
      removing.current = false;
      setBusy("");
    }
  }, [onChanged, t]);

  return (
    <div className="qw-card" data-testid="company-logo-card">
      <div style={{ fontWeight: 600, marginBottom: 12 }}>
        {t("company.section_identity")}
      </div>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 16,
          alignItems: "center",
        }}
      >
        <div
          style={{
            width: 88,
            height: 88,
            borderRadius: 12,
            border: "1px solid #e2e8f0",
            background: "#f8fafc",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            flex: "0 0 auto",
            fontWeight: 700,
            color: "#334155",
          }}
          data-testid="company-logo-preview"
        >
          {settings.logo_url ? (
            <img
              src={settings.logo_url}
              alt={t("company.logo_alt")}
              style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
              data-testid="company-logo-image"
            />
          ) : (
            <span data-testid="company-logo-fallback">
              {(settings.name || "").trim().charAt(0) || "—"}
            </span>
          )}
        </div>
        <div style={{ minWidth: 0, flex: "1 1 220px" }}>
          <div style={{ fontWeight: 600 }}>{t("company.logo")}</div>
          <div className="qw-help">{t("company.logo_hint")}</div>
          {!settings.has_logo && (
            <div className="qw-help" data-testid="company-logo-empty">
              {t("company.logo_empty")}
            </div>
          )}
          {canEdit && (
            <div
              style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}
            >
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={handleFile}
                style={{ display: "none" }}
                data-testid="company-logo-input"
              />
              <button
                type="button"
                className="qw-btn primary"
                onClick={() => fileRef.current?.click()}
                disabled={busy !== ""}
                data-testid="company-logo-upload"
              >
                {busy === "upload"
                  ? t("company.logo_uploading")
                  : settings.has_logo
                    ? t("company.logo_replace")
                    : t("company.logo_upload")}
              </button>
              {settings.has_logo && (
                <button
                  type="button"
                  className="qw-btn ghost"
                  onClick={() => setConfirmOpen(true)}
                  disabled={busy !== ""}
                  data-testid="company-logo-remove"
                >
                  {t("company.logo_remove")}
                </button>
              )}
            </div>
          )}
          {notice && (
            <div
              className="qw-help"
              style={{ color: "#059669" }}
              data-testid="company-logo-notice"
            >
              {notice}
            </div>
          )}
          {error && (
            <div className="qw-error" data-testid="company-logo-error">
              {error}
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title={t("company.logo_remove_title")}
        body={t("company.logo_remove_body")}
        confirmLabel={t("company.logo_remove")}
        submittingLabel={t("company.logo_removing")}
        cancelLabel={t("common.cancel")}
        submitting={busy === "remove"}
        onConfirm={remove}
        onCancel={() => setConfirmOpen(false)}
        tone="danger"
        testId="company-logo-confirm"
      />
    </div>
  );
};

export default CompanyLogoCard;
